// src/pages/NuovaRichiestaPotenzialeClientePage.js
// Mini-registro personale delle Richieste Commerciali per potenziali clienti (senza commessa),
// create dall'utente che sta guardando. Solo il creatore può modificarle da qui — è l'unico
// posto dove queste richieste specifiche possono essere corrette/integrate (es. voce dimenticata),
// dato che il Registro in Gestione è di sola consultazione e queste RC non vivono in una commessa.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, getDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2 } from "lucide-react";
import { RCForm, STATI_RC } from "../components/RichiestaCommercialeTab";

export default function NuovaRichiestaPotenzialeClientePage() {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();

  const [orgInfo, setOrgInfo] = useState(null);
  const [richieste, setRichieste] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editRC, setEditRC] = useState(null);

  useEffect(() => {
    if (!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(snap => { if (snap.exists()) setOrgInfo(snap.data()); });
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId || !currentUser) return;
    return onSnapshot(
      query(collection(db,"richiesteCommerciali"), where("orgId","==",effectiveOrgId), where("compilatoreUid","==",currentUser.uid)),
      snap => setRichieste(snap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(r => r.autonoma || !r.projectId) // solo quelle senza commessa collegata
        .sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)))
    );
  }, [effectiveOrgId, currentUser]);

  const elimina = async id => {
    if (!await confirm("Eliminare questa richiesta?", { type:"danger" })) return;
    await deleteDoc(doc(db,"richiesteCommerciali",id));
    addToast("Eliminata","info");
  };

  if (showForm || editRC) {
    return <RCForm
      rc={editRC}
      projectId=""
      projectName=""
      codiceCommessa=""
      clienteNome=""
      orgId={effectiveOrgId}
      orgInfo={orgInfo}
      onClose={() => { setShowForm(false); setEditRC(null); }}
    />;
  }

  return (
    <div style={{padding:20, height:"100%", overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:6,flexWrap:"wrap"}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#1e1b4b",margin:0}}>💼 Richieste commerciali per potenziale cliente</h3>
        <button onClick={()=>setShowForm(true)} className="btn btn-primary"
          style={{display:"flex",alignItems:"center",gap:5,background:"#d97706",borderColor:"#d97706",padding:"5px 12px",fontSize:12}}>
          <Plus size={13}/> Nuova richiesta
        </button>
        <span style={{fontSize:11,color:"#9ca3af",marginLeft:"auto"}}>{richieste.length} richiesta/e create da te</span>
      </div>
      <p style={{fontSize:11.5,color:"#9ca3af",marginBottom:16}}>
        Solo le richieste che hai creato tu, per potenziali clienti senza una commessa. Per i clienti già esistenti con una commessa aperta, crea la richiesta dall'interno della commessa stessa.
      </p>

      {richieste.length===0 ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <div style={{fontSize:48,marginBottom:12,opacity:.15}}>💼</div>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna richiesta creata da te per ora</div>
          <div style={{fontSize:12,marginTop:4}}>Crea la prima richiesta per un potenziale cliente</div>
        </div>
      ) : (
        <div>
          {richieste.map(rc => {
            const stato = STATI_RC[rc.stato] || STATI_RC.bozza;
            return (
              <div key={rc.id} style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:10,background:"#fff",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}
                onClick={()=>setEditRC(rc)}>
                <div style={{fontFamily:"monospace",fontWeight:800,color:"#d97706",fontSize:15,flexShrink:0}}>{rc.numero||"—"}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>{rc.oggetto}</div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{rc.dataEmissione} · {rc.clienteNome||"Potenziale cliente senza nome"}{rc.assegnatarioNome && <span style={{marginLeft:8,color:"#d97706"}}>→ {rc.assegnatarioNome}</span>}</div>
                </div>
                <span style={{fontSize:11,padding:"3px 10px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700,flexShrink:0}}>{stato.label}</span>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:11,color:"#9ca3af"}}>Valore offerta</div>
                  <div style={{fontSize:16,fontWeight:800,color:"#059669"}}>€{(rc.totaleOfferta||0).toLocaleString("it-IT",{minimumFractionDigits:2})}</div>
                </div>
                <button onClick={e=>{e.stopPropagation();elimina(rc.id);}} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:4}}>
                  <Trash2 size={14}/>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
